import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApiRequestError, getStatus, postAbort, postComplete, postConsent, postInputs, postSession } from './api';
import type {
  Branding,
  ClientContext,
  ClientForm,
  CompleteResponse,
  DeviceLocationMode,
  FieldValue,
  JourneyStep,
  PlatformConsent,
  SessionResponse,
  StatusResponse,
} from './types';
import Shell from './components/Shell';
import { consentDwellMs, finishAttempt, startAttempt } from './behaviour';
import { cameraSnapshot } from './cameraProbe';
import { getDeviceId } from './deviceId';
import { collectDevice } from './deviceSignals';
import { requestPosition } from './geolocation';
import ConsentPage from './pages/ConsentPage';
import StartPage from './pages/StartPage';
import LocationRequiredPage from './pages/LocationRequiredPage';
import ProcessingPage from './pages/ProcessingPage';
import FormPage from './pages/FormPage';
import ThankYouPage from './pages/ThankYouPage';
import NotFoundPage from './pages/NotFoundPage';

// @aws-amplify/ui-react-liveness pulls in TensorFlow.js for on-device face
// detection (~1.8MB) — code-split so ConsentPage/StartPage don't pay for it
// before the person has even agreed to the check.
const LivenessPage = lazy(() => import('./pages/LivenessPage'));

type Phase =
  | 'loading'
  | 'notfound'
  | 'expired'
  | 'consent'
  | 'start'
  | 'locationRequired'
  | 'liveness'
  | 'processing'
  | 'form'
  | 'thankyou'
  | 'waiting';

// RETRY never says why (reasonCode is always CAPTURE_RETRY, D4) — only how many tries are left.
interface RetryInfo {
  attemptsRemaining: number;
}

const POLL_INTERVAL_MS = 2000;

// Older responses (or a link without a journey) may omit `step`; DELIVERED then means done.
function stepOf(resp: StatusResponse): JourneyStep {
  return resp.step ?? (resp.status === 'DELIVERED' ? 'done' : 'processing');
}

export default function VerificationFlow() {
  const { token } = useParams<{ token: string }>();
  const [phase, setPhase] = useState<Phase>('loading');
  const [branding, setBranding] = useState<Branding | null>(null);
  const [consentText, setConsentText] = useState<string | null>(null);
  const [locale, setLocale] = useState<string>('es-GT');
  // Device & network context add-on (PLAN-Device-Context.md §B4). Without it
  // only the camera block is ever sent.
  const [captureContext, setCaptureContext] = useState(false);
  const [deviceLocation, setDeviceLocation] = useState<DeviceLocationMode>('OFF');
  const [platformConsent, setPlatformConsent] = useState<PlatformConsent | null>(null);
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [retryInfo, setRetryInfo] = useState<RetryInfo | null>(null);
  // Verification journeys (PLAN-Verification-Journeys.md §5.2): what comes after the capture.
  const [form, setForm] = useState<ClientForm | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string> | null>(null);
  const [done, setDone] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Once the capture is over the page follows `step` — never a verdict (D4).
  const enterStep = useCallback((resp: StatusResponse) => {
    const step = stepOf(resp);
    if (step === 'form' && resp.form) {
      setForm(resp.form);
      setFormErrors(null);
      setError(null);
      setPhase('form');
    } else if (step === 'done') {
      setRedirectUrl(resp.redirectUrl ?? null);
      setDone(true);
      setPhase('thankyou');
    } else {
      setPhase('processing');
    }
  }, []);

  // Initial load — figure out where in the flow this link currently is.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    getStatus(token)
      .then((resp) => {
        if (cancelled) return;
        setBranding(resp.branding);
        setConsentText(resp.consentText ?? null);
        setLocale(resp.locale);
        setCaptureContext(!!resp.captureContext);
        setDeviceLocation(resp.deviceLocation ?? 'OFF');
        setPlatformConsent(resp.platformConsent ?? null);

        switch (resp.status) {
          case 'CREATED':
          case 'OPENED':
            setPhase('consent');
            break;
          case 'CONSENTED':
          case 'LIVENESS_FAILED':
            setPhase('start');
            break;
          case 'SESSION_STARTED':
            // Orphaned from a page reload mid-check — nothing actionable
            // client-side; the sweeper reclaims it on its own schedule.
            setPhase('waiting');
            break;
          case 'LIVENESS_PASSED':
          case 'ABANDONED':
          case 'RESOLVING':
          case 'DELIVERED':
            enterStep(resp);
            break;
          case 'EXPIRED':
            setPhase('expired');
            break;
          default:
            setPhase('waiting');
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof ApiRequestError && e.status === 404) {
          setPhase('notfound');
        } else if (e instanceof ApiRequestError && e.status === 410) {
          setPhase('expired');
        } else {
          setError('No se pudo conectar con el servicio de verificación. Revisa tu conexión e intenta de nuevo.');
          setPhase('notfound');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token, enterStep]);

  // Poll every 2 s while resolve.py (and the journey's actions) run asynchronously:
  // - 'processing': until the step becomes 'form' or 'done';
  // - 'thankyou' after the form was sent: until 'done', so the redirect waits for delivery.
  const polling = phase === 'processing' || (phase === 'thankyou' && !done);
  useEffect(() => {
    if (!polling || !token) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      try {
        const resp = await getStatus(token);
        if (cancelled) return;
        if (resp.status === 'EXPIRED') {
          setPhase('expired');
          return;
        }
        const step = stepOf(resp);
        if (phase === 'processing') {
          if (!resp.step && (resp.status === 'CONSENTED' || resp.status === 'LIVENESS_FAILED')) {
            // An /abort that had nothing to abort: the link is still waiting for a capture.
            setPhase('start');
            return;
          }
          if (step !== 'processing') {
            enterStep(resp);
            return;
          }
        } else if (step === 'done') {
          // Thank-you page: the answers are in; only 'done' matters now.
          setRedirectUrl(resp.redirectUrl ?? null);
          setDone(true);
          return;
        }
      } catch (e: unknown) {
        if (cancelled) return;
        if (e instanceof ApiRequestError && e.status === 410) {
          setPhase('expired');
          return;
        }
        // transient network hiccup — keep polling
      }
      if (!cancelled) timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [polling, phase, token, enterStep]);

  const handleAcceptConsent = useCallback(async () => {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      const client: ClientContext | undefined = captureContext
        ? { deviceId: getDeviceId(), device: await collectDevice(), behaviour: { consentDwellMs: consentDwellMs() } }
        : undefined;
      await postConsent(token, platformConsent?.version, client);
      setPhase('start');
    } catch (e: unknown) {
      if (e instanceof ApiRequestError && e.status === 409) {
        setError('Los términos se actualizaron. Recarga la página para verlos e intenta de nuevo.');
      } else {
        setError('No se pudo registrar tu consentimiento. Intenta de nuevo.');
      }
    } finally {
      setBusy(false);
    }
  }, [token, captureContext, platformConsent]);

  const handleStart = useCallback(async () => {
    if (!token) return;
    // Before any await: iOS only shows the location prompt from inside the click.
    const locationPromise = captureContext && deviceLocation !== 'OFF' ? requestPosition() : undefined;
    setBusy(true);
    setError(null);
    try {
      let client: ClientContext | undefined;
      if (captureContext) {
        const [device, location] = await Promise.all([collectDevice(), locationPromise]);
        client = { device, location, deviceId: getDeviceId() };
      }
      const resp = await postSession(token, client);
      setSession(resp);
      if (captureContext) startAttempt(true);
      setPhase('liveness');
    } catch (e: unknown) {
      if (e instanceof ApiRequestError && e.status === 412 && e.code === 'LOCATION_REQUIRED') {
        setPhase('locationRequired');
      } else if (e instanceof ApiRequestError && e.status === 409) {
        setError('No quedan más intentos disponibles para este enlace.');
      } else if (e instanceof ApiRequestError && e.status === 402) {
        setError('No fue posible iniciar la verificación en este momento.');
      } else {
        setError('No se pudo iniciar la verificación. Intenta de nuevo.');
      }
    } finally {
      setBusy(false);
    }
  }, [token, captureContext, deviceLocation]);

  // Camera always; behaviour only with the add-on (finishAttempt() is a no-op otherwise).
  const attemptClient = useCallback(
    (): ClientContext => ({ camera: cameraSnapshot(), behaviour: captureContext ? finishAttempt() : undefined }),
    [captureContext],
  );

  const handleFinalizeResponse = useCallback((resp: CompleteResponse | { status: string }) => {
    if (resp.status === 'RETRY') {
      const r = resp as CompleteResponse;
      setRetryInfo({ attemptsRemaining: r.attemptsRemaining ?? 0 });
      setSession(null);
      setPhase('start');
    } else {
      // 'PROCESSING' (capture over, whatever the verdict) — the poller above
      // follows `step` from here: processing → (form) → done.
      setPhase('processing');
    }
  }, []);

  const handleAnalysisComplete = useCallback(async () => {
    if (!token || !session) return;
    try {
      const resp = await postComplete(token, session.sessionId, attemptClient());
      handleFinalizeResponse(resp);
    } catch {
      setError('No pudimos enviar tu captura. Revisa tu conexión e intenta de nuevo.');
      setPhase('start');
    }
  }, [token, session, handleFinalizeResponse, attemptClient]);

  // The person's answers (POST /inputs). Accepted or nothing pending → thank-you
  // page, which keeps polling until 'done' so the redirect happens after delivery.
  const handleSubmitInputs = useCallback(
    async (values: Record<string, FieldValue>) => {
      if (!token) return;
      setBusy(true);
      setError(null);
      try {
        const res = await postInputs(token, values);
        if (res.kind === 'invalid') {
          setFormErrors(res.errors);
          setError('Revisa los campos marcados.');
        } else {
          setDone(false);
          setPhase('thankyou');
        }
      } catch {
        setError('No pudimos enviar tus respuestas. Revisa tu conexión e intenta de nuevo.');
      } finally {
        setBusy(false);
      }
    },
    [token],
  );

  const handleLivenessError = useCallback(
    async (errorState: string) => {
      if (!token) return;
      try {
        const resp = await postAbort(token, errorState, attemptClient());
        handleFinalizeResponse(resp);
      } catch {
        setError('Ocurrió un problema con la cámara o la conexión. Intenta de nuevo.');
        setPhase('start');
      }
    },
    [token, handleFinalizeResponse, attemptClient],
  );

  if (phase === 'loading') {
    return (
      <Shell branding={branding}>
        <div className="processing">
          <div className="spinner" aria-hidden="true" />
        </div>
      </Shell>
    );
  }

  if (phase === 'notfound') return <NotFoundPage message={error ?? undefined} />;

  if (phase === 'expired') {
    return (
      <Shell branding={branding}>
        <h1>Enlace expirado</h1>
        <p>Este enlace de verificación ya no está disponible. Solicita uno nuevo.</p>
      </Shell>
    );
  }

  if (phase === 'waiting') {
    return (
      <Shell branding={branding}>
        <h1>Verificación en espera</h1>
        <p>Tu sesión anterior no se completó correctamente. Intenta abrir este enlace de nuevo en unos minutos.</p>
      </Shell>
    );
  }

  if (phase === 'consent' && branding) {
    return (
      <ConsentPage
        branding={branding}
        consentText={consentText}
        platformConsent={platformConsent}
        busy={busy}
        error={error}
        onAccept={handleAcceptConsent}
      />
    );
  }

  if (phase === 'start' && branding) {
    return (
      <StartPage
        branding={branding}
        busy={busy}
        error={error}
        retryInfo={retryInfo}
        deviceLocation={deviceLocation}
        onStart={handleStart}
      />
    );
  }

  if (phase === 'locationRequired' && branding) {
    return <LocationRequiredPage branding={branding} busy={busy} onRetry={handleStart} />;
  }

  if (phase === 'liveness' && branding && session) {
    return (
      <Suspense fallback={<ProcessingPage branding={branding} title="Preparando la cámara…" />}>
        <LivenessPage
          branding={branding}
          session={session}
          locale={locale}
          onAnalysisComplete={handleAnalysisComplete}
          onLivenessError={handleLivenessError}
        />
      </Suspense>
    );
  }

  if (phase === 'processing') {
    return <ProcessingPage branding={branding} />;
  }

  if (phase === 'form' && branding && form) {
    return (
      <FormPage
        branding={branding}
        form={form}
        busy={busy}
        error={error}
        serverErrors={formErrors}
        onSubmit={handleSubmitInputs}
      />
    );
  }

  if (phase === 'thankyou' && branding) {
    return <ThankYouPage branding={branding} done={done} redirectUrl={redirectUrl} />;
  }

  return (
    <Shell branding={branding}>
      <div className="processing">
        <div className="spinner" aria-hidden="true" />
      </div>
    </Shell>
  );
}
